import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { computeFordModel } from '../../utils/forecastHelper.js'
import { actionCreators as fordForecastActionCreators } from '../../store/FordForecast'
import { actionCreators as selectionsActionCreators } from '../../store/Selections'
import { actionCreators as GlobalStoreCreator } from '../../store/General'
import { actionCreators as modelActionCreators } from '../../store/Model'
import { actionCreators as filterActionCreators } from '../../store/Filter'
import { EditAreaAttsU833 } from './EditAreaAttsU833.js'
// import { EditAreaSliders } from './EditAreaSliders.js'   // commented out in your original
import './ScenarioEditor.scss'
import { FilterChooser } from '../filter/FilterChooser.js'

export const ScenarioEditorFordU = (props) => {
  const [lo_mid_hi, setLo_mid_hi] = useState(2)

  const dispatch = useDispatch()

  const study = useSelector((state) => state.study)
  const auth = useSelector((state) => state.auth)
  const model = useSelector((state) => state.model)
  const selection = useSelector((state) => state.selection)
  const filter = useSelector((state) => state.filter)
  const modelResults = useSelector((state) => state.forecaster?.fordForecast)

  const studyId = study?.uid
  const fordSelector = selection?.u833
  const filtersDatasetId = props?.filtersDatasetId || 'fordu833_filters'

  const activeConfig = props?.models?.[0]?.name ?? null
  const _config = model[activeConfig]?.config

  // Determine current mode from activeCategory or actual selections
  const activeCategory = (filter?.activeCategory || 'Consumer').toLowerCase()
  const currentSelected = filtersDatasetId 
    ? filter?.selected?.[filtersDatasetId] 
    : filter?.selectedFilter

  // Helper: detect if the current filter looks like "fleet mode"
  const isFleetMode = () => {
    if (!currentSelected?.syntax) return false
    
    const syntaxLower = currentSelected.syntax.toLowerCase()
    return (
      syntaxLower.includes('seg02') ||
      syntaxLower.includes('fleet') ||
      syntaxLower.includes('seg59') ||  // from your log example
      (currentSelected.name && currentSelected.name.toLowerCase().includes('fleet'))
    )
  }

  // Derived mode (prefer explicit category, fallback to detected from selections)
  const effectiveMode = activeCategory.includes('fleet') || isFleetMode()
    ? 'fleet'
    : 'consumer'

  // Sync category when selections change (so drawer tab + button stay aligned)
  useEffect(() => {
    const targetCategory = effectiveMode === 'fleet' ? 'Fleet' : 'Consumer'

    if (filter?.activeCategory !== targetCategory) {
      dispatch(filterActionCreators.setActiveFilterCategory(targetCategory))
      // Optional: auto-trigger forecast refresh on mode change
      // dispatch(GlobalStoreCreator.setGlobalStore('forecast_needed', true))
    }
  }, [effectiveMode, filter?.activeCategory, dispatch])

  // Treat "Total Population" (1==1) as no selection yet – default to Consumer with Consumer button selected
  const isTotalPopulation = (sel) =>
    !sel?.syntax || sel.syntax === '1==1' || (sel.autoLabel || sel.label || '').toLowerCase() === 'total population'

  // Initialize defaults when nothing exists yet or current filter is still Total Population
  useEffect(() => {
    if (!filter?.activeCategory && (!currentSelected?.syntax || isTotalPopulation(currentSelected))) {
      dispatch(filterActionCreators.setActiveFilterCategory('Consumer'))
      dispatch(
        filterActionCreators.setFilterDefaults(
          {
            filterAlwaysIn: 'Seg01',
            defaultFilterLabel: 'Consumers',
            selectedFilter: {
              autoLabel: 'Consumers',
              syntax: 'Seg01==1',
              label: 'Consumers',
              name: 'Seg01',
              n: null,
            },
            selected: {
              autoLabel: 'Consumers',
              syntax: 'Seg01==1',
              label: 'Consumers',
              name: 'Seg01',
              n: null,
            },
          },
          filtersDatasetId
        )
      )
    }
  }, [filter?.activeCategory, currentSelected?.syntax, currentSelected?.autoLabel, currentSelected?.label, dispatch, filtersDatasetId])

  // Re-refresh sample size when selected filter changes
  useEffect(() => {
    if (currentSelected?.syntax && (currentSelected.n == null || currentSelected.n === undefined)) {
      dispatch(filterActionCreators.refreshSampleSizes(filtersDatasetId))
    }
  }, [currentSelected?.syntax, currentSelected?.n, filtersDatasetId, dispatch])

  useEffect(() => {
    if (activeConfig && !model[activeConfig]) {
      console.warn(
        `Model ${activeConfig} is missing from store, attempting to fetch...`
      )
      dispatch(modelActionCreators.getModel(activeConfig))
    }
  }, [activeConfig, model, dispatch])

  useEffect(() => {
    if (!fordSelector && _config?.play?.length > 0) {
      dispatch(selectionsActionCreators.setSelection('u833', _config.play[0]))
    }
  }, [_config, fordSelector, dispatch])

  // Button actions – switch defaults + category
  const setConsumer = () => {
    dispatch(filterActionCreators.deselectAll(filtersDatasetId))
    
    setTimeout(() => {
      dispatch(
        filterActionCreators.setFilterDefaults(
          {
            filterAlwaysIn: 'Seg01',
            defaultFilterLabel: 'Consumers',
            selectedFilter: {
              autoLabel: 'Consumers',
              syntax: 'Seg01==1',
              label: 'Consumers',
              name: 'Seg01',
              n: null,
            },
            selected: {
              autoLabel: 'Consumers',
              syntax: 'Seg01==1',
              label: 'Consumers',
              name: 'Seg01',
              n: null,
            },
          },
          filtersDatasetId
        )
      )
      dispatch(filterActionCreators.setActiveFilterCategory('Consumer'))
      dispatch(GlobalStoreCreator.setGlobalStore('forecast_needed', true))
    }, 0)
  }

  const setFleet = () => {
    dispatch(filterActionCreators.deselectAll(filtersDatasetId))
    
    setTimeout(() => {
      dispatch(
        filterActionCreators.setFilterDefaults(
          {
            filterAlwaysIn: 'Seg02',
            defaultFilterLabel: 'Fleet',
            selectedFilter: {
              autoLabel: 'Fleet',
              syntax: 'Seg02==1',
              label: 'Fleet',
              name: 'Seg02',
              n: null,
            },
            selected: {
              autoLabel: 'Fleet',
              syntax: 'Seg02==1',
              label: 'Fleet',
              name: 'Seg02',
              n: null,
            },
          },
          filtersDatasetId
        )
      )
      dispatch(filterActionCreators.setActiveFilterCategory('Fleet'))
      dispatch(GlobalStoreCreator.setGlobalStore('forecast_needed', true))
    }, 0)
  }

  // Critical guards
  if (!activeConfig || !model[activeConfig]) {
    const msg = !activeConfig
      ? 'Missing activeConfig'
      : `Missing model[${activeConfig}]`
    return <div>Loading model configuration... ({msg})</div>
  }

  if (filtersDatasetId && !filter?.selected?.[filtersDatasetId]) {
    return <div>Loading filters...</div>
  }

  const getCurrentFilter = () => {
    let the_filter = { name: 'total' }
    if (filtersDatasetId && filter?.selected?.[filtersDatasetId]) {
      the_filter = filter.selected[filtersDatasetId]
    } else if (filter?.selectedFilter) {
      the_filter = filter.selectedFilter
    }
    return the_filter
  }

  const Forecast = async () => {
    const prodDef_atts = _config?.productDef?.atts || []
    if (!prodDef_atts.length) return

    const base_model = model[activeConfig]
    if (!base_model?.config) {
      console.error('Base model configuration not available')
      return
    }

    let the_filter =
      (filtersDatasetId
        ? filter.selected[filtersDatasetId]
        : filter.selectedFilter) || {}

    dispatch({
      type: 'FORD_FORECAST_REQUEST',
      modelName: 'model_fordu833_base',
      playHash: base_model?.config?.playHash,
      filterName: the_filter.name,
    })

    let results = {}
    let addonRev = {}
    let pricing = {}

    for (let i = 0; i < prodDef_atts.length; i++) {
      const att = prodDef_atts[i]
      if (att.readOnly || att.type !== 'discrete') continue

      addonRev[att.name] = {}

      const isModel2 = [
        'utility',
        'premium',
        'cold',
        'accessory',
        'software',
        'charging',
      ].includes(att.name)

      if (att.name === 'base') {
        const base_index = parseInt(base_model?.config?.play[0]['base_01'], 10)

        const payload = {
          config:
            base_model?.config?.play.map((item) => {
              const newItem = { ...item, uid: 'Base' }

              Object.keys(newItem).forEach((key) => {
                if (/_\d{2}$/.test(key)) {
                  const attDef = prodDef_atts.find((a) => a.name === key)
                  if (attDef && attDef.levels) {
                    const val = parseInt(newItem[key], 10)
                    const levelObj = attDef.levels.find((l) => l.value == val)
                    if (levelObj && levelObj.label) {
                      const price =
                        parseFloat(levelObj.label.replace(/[$,]/g, '')) || 0
                      newItem[key] = price
                    }
                  }
                }
              })

              return newItem
            }) || [],
          filter: the_filter,
          modelInfo: base_model?.modelInfo,
          playHash: base_model?.config?.playHash,
        }

        const priceLabel =
          prodDef_atts
            .find((a) => a.name === 'base_01')
            ?.levels?.find((l) => l.value === base_index)?.label || 'Base Price'

        const levelPrice = parseFloat(priceLabel.replace(/[$,]/g, '')) || 0
        pricing['Final Configured Vehicle'] = levelPrice

        try {
          const result = await computeFordModel(studyId, auth, payload)

          if (result && 'other' in result) {
            delete result['other']
          }
          results[att.label] = result
        } catch (error) {
          console.error('Error computing Ford model for base:', error)
        }
        continue
      }

      let play_config = []

      for (let lev_count = 0; lev_count < att.levels.length; lev_count++) {
        const level = att.levels[lev_count]
        const levelLabel = level.label
        const att_price_name =
          lev_count + 1 >= 10
            ? `${att.name}_${lev_count + 1}`
            : `${att.name}_0${lev_count + 1}`

        const productConfig = {
          uid: isModel2 ? 'prod1' : `prod${lev_count + 1}`,
          label: levelLabel,
          available: 1,
          base_01: base_model?.config?.play[0]?.['base_01'] || 1,
          [`${att.name}`]: lev_count + 1,
        }

        const priceAtt = base_model?.config?.productDef?.atts?.find(
          (a) => a.name === att_price_name
        )

        const selected_price_level =
          parseInt(base_model?.config?.play[0]?.[att_price_name], 10) || 1

        const priceLabel =
          prodDef_atts
            .find((a) => a.name === att_price_name)
            ?.levels?.find((l) => l.value === selected_price_level)?.label ||
          '$0'

        const levelPrice = parseFloat(priceLabel.replace(/[$,]/g, '')) || 0
        pricing[levelLabel] = levelPrice

        if (priceAtt && priceAtt.levels?.length > 1) {
          productConfig[att_price_name] = levelPrice
        }

        if (!isModel2) play_config.push(productConfig)

        if (isModel2) {
          const perLevelKey = `model_fordu833_${att.name}${lev_count + 1}`
          const sharedKey = `model_fordu833_${att.name}`
          const resolvedModel = model[perLevelKey] || model[sharedKey]

          let share = 0

          if (resolvedModel?.modelInfo) {
            const payload = {
              etag: resolvedModel.modelInfo?.name,
              config: [productConfig],
              filter: the_filter,
              modelInfo: resolvedModel.modelInfo,
              playHash: base_model.config.playHash,
            }

            try {
              const result = await computeFordModel(studyId, auth, payload)
              share = result?.prod1?.share ?? 0
            } catch (error) {
              console.error(
                `Error computing Ford model for ${att.name} ${levelLabel}:`,
                error
              )
              share = 0
            }
          }

          if (!results[att.label]) results[att.label] = {}
          results[att.label][levelLabel] = { share }
          addonRev[att.name][levelLabel] = share * levelPrice
        }
      }

      if (!isModel2) {
        const sharedKey = `model_fordu833_${att.name}`
        const resolvedModel = model[sharedKey]

        let attResults = {}

        if (resolvedModel?.modelInfo) {
          const payload = {
            etag: resolvedModel.modelInfo?.name,
            config: play_config,
            filter: the_filter,
            modelInfo: resolvedModel.modelInfo,
            playHash: base_model.config.playHash,
          }

          try {
            const result = await computeFordModel(studyId, auth, payload)

            for (const uid in result) {
              const metrics = result[uid]
              const product = play_config.find((p) => p.uid === uid)
              const label = product?.label || uid

              const share = metrics?.share || 0
              const levelPrice = pricing[label] || 0
              attResults[label] = { share }
              addonRev[att.name][label] = share * levelPrice
            }
          } catch (error) {
            console.error('Error computing Ford model for Model1:', error)
          }
        }

        if (Object.keys(attResults).length === 0 && play_config.length > 0) {
          play_config.forEach((product) => {
            const label = product.label
            attResults[label] = { share: 0 }
            addonRev[att.name][label] = 0
          })
        }

        results[att.label] = attResults
      }
    }

    dispatch(
      fordForecastActionCreators.setForecast({
        data: results,
        playHash: base_model?.config?.playHash,
        filterName: the_filter.name,
        activeConfig: 'model_fordu833_base',
        products: base_model?.config?.play,
      })
    )

    dispatch(
      fordForecastActionCreators.saveAddOnNumber(
        'model_fordu833_base',
        the_filter.name,
        addonRev
      )
    )
    dispatch(GlobalStoreCreator.setGlobalStore('fordu833_pricing', pricing))
    dispatch(GlobalStoreCreator.setGlobalStore('forecast_running', false))
  }

  const setPricing = async (val, lo_mid_hi_val) => {
    dispatch(GlobalStoreCreator.setGlobalStore('forecast_needed', true))
    await dispatch(modelActionCreators.setPricingTo(activeConfig, val))
    setLo_mid_hi(lo_mid_hi_val)
  }

  // Validation: power_02 must be < power_03, interior_02 must be < interior_03 (compare by numeric label e.g. price, else by level value)
  const currentProduct = _config?.play?.find((p) => p.uid === fordSelector?.uid) || _config?.play?.[0]
  const prodDef_atts = _config?.productDef?.atts || []
  const getLevelLabel = (attName, value) => {
    const att = prodDef_atts.find((a) => a.name === attName)
    const level = att?.levels?.find((l) => l.value == value)
    return level?.label ?? attName
  }
  // Display label for _02/_03: use base attribute's level label (e.g. "interior" level 2 = "Soft Synthetic Leather Seats") so message shows option names, not prices
  const getDisplayLabelForOption = (priceAttName) => {
    const match = priceAttName.match(/^(.+)_0?(\d+)$/)
    if (!match) return getLevelLabel(priceAttName, currentProduct[priceAttName])
    const [, baseName, levelIndex] = match
    const baseAtt = prodDef_atts.find((a) => a.name === baseName)
    const level = baseAtt?.levels?.find((l) => l.value == levelIndex)
    return level?.label ?? getLevelLabel(priceAttName, currentProduct[priceAttName])
  }
  const labelAsNumber = (label) => {
    if (label == null) return NaN
    const s = String(label).replace(/[$%,\s]/g, '')
    const n = parseFloat(s)
    return isNaN(n) ? NaN : n
  }
  const isInvalidOrder = (att02, att03) => {
    const label02 = getLevelLabel(att02, currentProduct[att02])
    const label03 = getLevelLabel(att03, currentProduct[att03])
    const num02 = labelAsNumber(label02)
    const num03 = labelAsNumber(label03)
    if (!isNaN(num02) && !isNaN(num03)) return num02 >= num03
    return Number(currentProduct[att02]) >= Number(currentProduct[att03])
  }
  const powertrainErr =
    currentProduct != null &&
    prodDef_atts.some((a) => a.name === 'power_02') &&
    isInvalidOrder('power_02', 'power_03')
      ? {
          label02: getDisplayLabelForOption('power_02'),
          label03: getDisplayLabelForOption('power_03'),
        }
      : null
  const interiorErr =
    currentProduct != null &&
    prodDef_atts.some((a) => a.name === 'interior_02') &&
    isInvalidOrder('interior_02', 'interior_03')
      ? {
          label02: getDisplayLabelForOption('interior_02'),
          label03: getDisplayLabelForOption('interior_03'),
        }
      : null
  const forecastValidationFailed = !!(powertrainErr || interiorErr)

  useEffect(() => {
    dispatch(GlobalStoreCreator.setGlobalStore('price_sens_no_forecast', forecastValidationFailed))
  }, [forecastValidationFailed, dispatch])

  return (
    <div className="scenario-editor" style={props.style}>
      <div className="tab-section">
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 4,
            marginBottom: 10,
          }}
        >
          <button
            className="btn inline-block"
            style={{ width: '32%' }}
            role="button"
            aria-label="apply changes"
            onClick={Forecast}
            disabled={forecastValidationFailed}
          >
            <i className="fal fa-rocket-launch" style={{ marginRight: 5 }}></i>{' '}
            FORECAST
          </button>
          {powertrainErr && (
            <div style={{ color: 'red', fontSize: 12, width: '100%', textAlign: 'right' }}>
              {powertrainErr.label02} must have lower price than {powertrainErr.label03}.
            </div>
          )}
          {interiorErr && (
            <div style={{ color: 'red', fontSize: 12, width: '100%', textAlign: 'right' }}>
              {interiorErr.label02} must have lower price than {interiorErr.label03}.
            </div>
          )}
        </div>
      </div>

      <div
        className="product-selector"
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderTop: '1px dashed var(--border-color)',
          borderBottom: '1px dashed var(--border-color)',
          paddingBottom: 15,
          marginBottom: 10,
        }}
      >
        <h3 style={{ color: 'var(--text-color)', paddingTop: 10 }}>
          Selection
        </h3>
        <div style={{ display: 'flex', gap: 5 }}>
          <button
            className={`btn inline-block ${effectiveMode === 'consumer' ? 'sel' : ''}`}
            style={{ width: '25%' }}
            role="button"
            aria-label="CONSUMER"
            onClick={setConsumer}
          >
            <i className="fal fa-users" style={{ marginRight: 5 }}></i> CONSUMER
          </button>
          <button
            className={`btn inline-block ${effectiveMode === 'fleet' ? 'sel' : ''}`}
            style={{ width: '25%' }}
            role="button"
            aria-label="FLEET"
            onClick={setFleet}
          >
            <i className="fal fa-building" style={{ marginRight: 5 }}></i> FLEET
          </button>
        </div>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', gap: 5 }}
        >
          <div style={{ width: '100%', marginTop: 15 }}>
            <span
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 500,
                height: 15,
                textTransform: 'uppercase',
                color: 'var(--text-color)',
              }}
            >
              Filters
            </span>
            <FilterChooser
              mini={false}
              datasetId={filtersDatasetId}
              custom={effectiveMode === 'consumer' ? 'Consumer' : 'Fleet'}
              hiddenCategories={effectiveMode === 'consumer' ? ['Fleet'] : ['Consumer']}
              style={{ width: '100%', margin: 0 }}
              showN={true}
            />
          </div>
        </div>
      </div>

      <div className="editor-content">
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 5,
            marginRight: 20,
          }}
        >
          <button
            className={`levelBtns ${lo_mid_hi === 1 ? 'sel' : ''}`}
            role="button"
            aria-label="set low"
            onClick={() => setPricing(1, 1)}
          >
            <i
              className="fal fa-tachometer-slowest"
              style={{ marginRight: 5 }}
            ></i>{' '}
            LOW
          </button>
          <button
            className={`levelBtns ${lo_mid_hi === 2 ? 'sel' : ''}`}
            role="button"
            aria-label="set mid"
            onClick={() => setPricing(3, 2)}
          >
            <i
              className="fal fa-tachometer-average"
              style={{ marginRight: 5 }}
            ></i>{' '}
            MID
          </button>
          <button
            className={`levelBtns ${lo_mid_hi === 3 ? 'sel' : ''}`}
            role="button"
            aria-label="set high"
            onClick={() => setPricing(5, 3)}
          >
            <i
              className="fal fa-tachometer-fastest"
              style={{ marginRight: 5 }}
            ></i>{' '}
            HIGH
          </button>
        </div>

        <EditAreaAttsU833
          prodId={fordSelector?.uid}
          configModel={activeConfig}
          monitor_atts={null}
        />
      </div>
    </div>
  )
}